import type { Spread, Tier } from './types.js';

/**
 * Everything the UI and the API gate on. Both sides import this same function
 * so a button that is hidden on the client is also refused by the server.
 */
export interface Entitlements {
  tier: Tier;
  /** gifted / mystic / oracle — the three that unlock the full app. */
  paid: boolean;
  /** Deck slugs this tier may open. `'*'` means every available deck. */
  decks: string[] | '*';
  /** The long-form curated meaning behind the "unlock" CTA. */
  inDepthMeanings: boolean;
  /** Personal notes on a card. */
  notes: boolean;
  /** Notes and readings sync to the server rather than living only on-device. */
  serverSync: boolean;
  /** Spread sizes that may be drawn. */
  spreads: Spread[];
  /**
   * Withholds the final card of a 3-card spread behind blur.webp. No current
   * tier does; kept so readings saved under the old free tier still render
   * their locked card.
   */
  locksFinalCard: boolean;
  /** The "what's on your mind" input. */
  askQuestion: boolean;
  /** Full AI synthesis of a reading. */
  aiSynthesis: boolean;
  /**
   * The free tier's one short Oracle interpretation per day, dealt and written
   * server-side (POST /readings/daily). Paid tiers get `aiSynthesis` instead.
   */
  dailyOracle: boolean;
  /** May turn reversals off, or change their frequency. */
  configurableReversals: boolean;
  /** Readings kept in history. */
  readingHistory: boolean;
}

const PAID_TIERS: readonly Tier[] = ['gifted', 'mystic', 'oracle'];

export function isPaidTier(tier: Tier): boolean {
  return PAID_TIERS.includes(tier);
}

/**
 * Tier precedence when several signals disagree, per SPEC:
 *   gifted > oracle > mystic (only while the subscription is live) > free > guest
 *
 * `gifted` wins outright because it is set by hand in the database and has no
 * Stripe customer at all; a lapsed subscription must never demote a gift.
 */
export function resolveTier(input: {
  gifted?: boolean;
  hasLifetimePurchase?: boolean;
  subscriptionActive?: boolean;
  authenticated?: boolean;
}): Tier {
  if (input.gifted) return 'gifted';
  if (input.hasLifetimePurchase) return 'oracle';
  if (input.subscriptionActive) return 'mystic';
  return input.authenticated ? 'free' : 'guest';
}

export function entitlementsFor(tier: Tier): Entitlements {
  const paid = isPaidTier(tier);

  if (paid) {
    return {
      tier,
      paid: true,
      decks: '*',
      inDepthMeanings: true,
      notes: true,
      serverSync: true,
      spreads: [1, 3, 5],
      locksFinalCard: false,
      askQuestion: true,
      aiSynthesis: true,
      dailyOracle: false,
      configurableReversals: true,
      readingHistory: true,
    };
  }

  // Free and guest share the same feature surface, except that the daily Oracle
  // reading needs an account (it is dealt by the server, and limited per user),
  // as does anything persisted server-side.
  return {
    tier,
    paid: false,
    decks: ['rws'],
    inDepthMeanings: false,
    notes: false,
    serverSync: tier === 'free',
    spreads: [3],
    locksFinalCard: false,
    askQuestion: false,
    aiSynthesis: false,
    dailyOracle: tier === 'free',
    configurableReversals: false,
    readingHistory: tier === 'free',
  };
}

export function canOpenDeck(tier: Tier, deckSlug: string): boolean {
  const { decks } = entitlementsFor(tier);
  return decks === '*' || decks.includes(deckSlug);
}

export function canDrawSpread(tier: Tier, spread: Spread): boolean {
  return entitlementsFor(tier).spreads.includes(spread);
}

/**
 * Which positions of a drawn spread are withheld from this tier.
 * When a tier does lock a card, it is hidden behind the pre-rendered blur
 * asset — never a CSS filter, which would ship the answer to anyone who opens
 * devtools. No tier locks one today (the free daily reading shows all three).
 */
export function lockedPositions(tier: Tier, spread: Spread): number[] {
  if (!entitlementsFor(tier).locksFinalCard) return [];
  return spread === 3 ? [2] : [];
}
